import { forwardRef, type ReactNode } from 'react';
import {
  CircularProgress,
  FormControl,
  HelperText,
  InputLabel,
  Select,
  type FormControlProps,
  type SelectProps,
} from '@/primitives';
import { EventType } from '@/lib/types';
import {
  compactFilledBorderedInputFixedBoxSx,
  compactFilledBorderedSelectDisplayPadding,
  compactFilledFieldHelperTextSx,
  compactFilledFloatingLabelCascadeSx,
  compactFilledFormControlSpacingSx,
  compactFilledInputValueSx,
  compactFilledSelectInputLabelSx,
  compactFilledSelectMenuPaperSx,
  defaultFilledFloatingLabelSx,
  defaultFilledInputValueSx,
} from '@/lib/compactFilledFieldStyles';

export type SelectFieldProps = SelectProps & {
  eventType?: EventType;
  fullWidth?: boolean;
  helperText?: string[];
  /** Smaller label, value text, and control height (entity / form field dropdowns). */
  inputCompact?: boolean;
  label: ReactNode;
  labelId: string;
  loading?: boolean;
  sx?: FormControlProps['sx'];
  trackEventName?: string;
  variant?: SelectProps['variant'];
  trackSelect?: (event: any) => void;
};

function LoadingIndicatorCompact() {
  return <CircularProgress variant="indeterminate" size={20} />;
}

function LoadingIndicatorDefault() {
  return <CircularProgress variant="indeterminate" size={24} />;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  function SelectField(
    {
      fullWidth,
      label,
      labelId,
      loading,
      sx,
      variant,
      helperText,
      inputCompact = false,
      trackSelect,
      trackEventName,
      eventType = EventType.DROPDOWN_SELECT,
      onChange,
      ...props
    },
    ref,
  ) {
    const handleChange: SelectFieldProps['onChange'] = (event, child) => {
      if (trackSelect && trackEventName) {
        trackSelect({
          name: trackEventName,
          properties: {
            eventType,
            fieldName: labelId,
            selectedValue: event.target.value,
          },
        });
      }
      if (onChange) {
        onChange(event, child);
      }
    };

    if (variant === 'filled') {
      const selectProps = props as SelectProps;
      const {
        slotProps: incomingSlotProps,
        sx: incomingSelectSx,
        MenuProps: incomingMenuProps,
        ...selectRest
      } = selectProps;

      const mergedMenuProps = inputCompact
        ? {
            ...incomingMenuProps,
            PaperProps: {
              ...incomingMenuProps?.PaperProps,
              sx: [
                compactFilledSelectMenuPaperSx,
                ...(incomingMenuProps?.PaperProps?.sx
                  ? Array.isArray(incomingMenuProps.PaperProps.sx)
                    ? incomingMenuProps.PaperProps.sx
                    : [incomingMenuProps.PaperProps.sx]
                  : []),
              ],
            },
          }
        : incomingMenuProps;

      const passedInput = incomingSlotProps?.input;
      const passedInputObj =
        passedInput && typeof passedInput === 'object' ? passedInput : {};

      // FilledInput root gets `Select` `sx` (same as TextField `InputProps.sx`).
      // `slotProps.input.sx` targets the inner slot only — putting height/border there
      // leaves the root at MUI's default small height, so dropdowns look shorter than text fields.
      const filledInputSx = {
        ...(inputCompact
          ? compactFilledInputValueSx
          : defaultFilledInputValueSx),
        ...(inputCompact ? compactFilledBorderedInputFixedBoxSx : {}),
        overflow: 'visible',
        boxSizing: 'border-box',
        bgcolor: 'background.paper',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: helperText == undefined ? 'divider' : 'error.main',
        ':focus-within': {
          borderColor: 'primary.main',
        },
        ':has(input[aria-invalid="true"])': {
          borderColor: 'error.main',
        },
        ...(inputCompact && {
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            fontSize: '0.8125rem',
            lineHeight: '18px',
            letterSpacing: '0.25px',
            ...compactFilledBorderedSelectDisplayPadding,
          },
        }),
        '&.Mui-disabled': {
          bgcolor: 'action.disabledBackground',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          fontStyle: 'italic',
          '& .MuiSelect-select': {
            bgcolor: 'transparent',
            WebkitTextFillColor: 'currentColor',
          },
        },
      };

      return (
        <FormControl
          fullWidth={fullWidth}
          disabled={selectRest.disabled}
          margin={inputCompact ? 'none' : undefined}
          sx={[
            { overflow: 'visible' },
            ...(inputCompact
              ? [
                  compactFilledFloatingLabelCascadeSx,
                  compactFilledFormControlSpacingSx,
                ]
              : []),
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          ]}
        >
          <InputLabel
            id={labelId}
            required={props.required}
            shrink
            variant="filled"
            error={!!helperText}
            sx={{
              ...(inputCompact
                ? {
                    ...compactFilledSelectInputLabelSx,
                    ':has(+ .Mui-disabled)': {
                      color: 'text.disabled',
                    },
                  }
                : {
                    ...defaultFilledFloatingLabelSx,
                    ':has(+ .Mui-disabled)': {
                      color: 'text.disabled',
                    },
                  }),
            }}
          >
            {label}
          </InputLabel>
          <Select
            {...selectRest}
            MenuProps={mergedMenuProps}
            disableUnderline
            IconComponent={
              loading
                ? inputCompact
                  ? LoadingIndicatorCompact
                  : LoadingIndicatorDefault
                : undefined
            }
            labelId={labelId}
            ref={ref}
            size={inputCompact ? 'small' : props.size}
            slotProps={{
              ...incomingSlotProps,
              input: passedInputObj,
            }}
            sx={[
              filledInputSx,
              {
                borderRadius: '8px',
                '& .MuiSelect-select': {
                  borderRadius: '8px !important',
                },
                '&.Mui-disabled .MuiSelect-icon': {
                  visibility: 'hidden',
                },
              },
              ...(incomingSelectSx
                ? Array.isArray(incomingSelectSx)
                  ? incomingSelectSx
                  : [incomingSelectSx]
                : []),
            ]}
            variant="filled"
            onChange={handleChange}
          />
          <HelperText
            helperText={helperText}
            sx={{
              color: 'error.main',
              ...(inputCompact ? compactFilledFieldHelperTextSx : {}),
            }}
          />
        </FormControl>
      );
    }

    return (
      <FormControl sx={sx}>
        <InputLabel id={labelId} variant={variant}>
          {label}
        </InputLabel>
        <Select
          {...props}
          label={label}
          labelId={labelId}
          ref={ref}
          variant={variant}
          onChange={handleChange}
        />
        <HelperText
          helperText={helperText}
          sx={{
            color: 'error.main',
            ...(inputCompact ? compactFilledFieldHelperTextSx : {}),
          }}
        />
      </FormControl>
    );
  },
);
